import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { 
  ArrowLeft, 
  Plus, 
  Pencil, 
  Trash2, 
  Calendar, 
  Briefcase, 
  MessageCircle,
  GripVertical,
  Eye,
  EyeOff
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import AdminSidebar from "@/components/dashboard/AdminSidebar";
import { dashboardItems } from "@/data/dashboardData";
import { SidebarProvider, SidebarInset, SidebarTrigger } from "@/components/ui/sidebar";
import { Menu } from "lucide-react";

interface NewsItem {
  id: string;
  text_en: string;
  text_ar: string;
  category: string;
  is_active: boolean;
  display_order: number;
  created_at: string;
}

const categoryConfig = {
  events: { 
    label: "Events & Exhibitions", 
    labelAr: "الفعاليات والمعارض", 
    icon: Calendar,
    color: "bg-primary/10 text-primary" 
  },
  courses: { 
    label: "Courses & Jobs", 
    labelAr: "الدورات والوظائف", 
    icon: Briefcase,
    color: "bg-blue-500/10 text-blue-600" 
  },
  social: { 
    label: "Social & X Spaces", 
    labelAr: "التواصل ومساحات X", 
    icon: MessageCircle,
    color: "bg-purple-500/10 text-purple-600" 
  },
};

const NewsTicker = () => {
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === "ar";
  
  const [items, setItems] = useState<NewsItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState<string | null>(null);
  const [editingItem, setEditingItem] = useState<NewsItem | null>(null);
  const [filterCategory, setFilterCategory] = useState<string>("all");
  const [tickerVisible, setTickerVisible] = useState(true);
  
  const [formData, setFormData] = useState({
    text_en: "",
    text_ar: "",
    category: "events" as "events" | "courses" | "social",
    is_active: true,
  });

  useEffect(() => {
    fetchItems();
    fetchTickerVisibility();
  }, []);

  const fetchTickerVisibility = async () => {
    const { data } = await supabase
      .from("section_visibility")
      .select("is_visible")
      .eq("section_key", "news_ticker")
      .single();
    
    if (data) {
      setTickerVisible(data.is_visible);
    }
  };

  const toggleTickerVisibility = async () => {
    const newValue = !tickerVisible;
    const { error } = await supabase
      .from("section_visibility")
      .update({ is_visible: newValue })
      .eq("section_key", "news_ticker");
    
    if (!error) {
      setTickerVisible(newValue);
      toast.success(
        newValue 
          ? (isRTL ? "تم تفعيل أشرطة الأخبار" : "News tickers enabled")
          : (isRTL ? "تم إخفاء أشرطة الأخبار" : "News tickers hidden")
      );
    }
  };

  const fetchItems = async () => {
    setIsLoading(true);
    const { data, error } = await supabase
      .from("news_ticker_items")
      .select("*")
      .order("category")
      .order("display_order");
    
    if (data && !error) {
      setItems(data);
    }
    setIsLoading(false);
  };

  const handleSubmit = async () => {
    if (!formData.text_en || !formData.text_ar) {
      toast.error(isRTL ? "يرجى ملء جميع الحقول" : "Please fill all fields");
      return;
    }

    if (editingItem) {
      const { error } = await supabase
        .from("news_ticker_items")
        .update({
          text_en: formData.text_en,
          text_ar: formData.text_ar,
          category: formData.category,
          is_active: formData.is_active,
        })
        .eq("id", editingItem.id);

      if (!error) {
        toast.success(isRTL ? "تم تحديث الخبر بنجاح" : "News item updated successfully");
        fetchItems();
        resetForm();
      }
    } else {
      const maxOrder = items.filter(i => i.category === formData.category).length;
      const { error } = await supabase
        .from("news_ticker_items")
        .insert({
          text_en: formData.text_en,
          text_ar: formData.text_ar,
          category: formData.category,
          is_active: formData.is_active,
          display_order: maxOrder + 1,
        });

      if (!error) {
        toast.success(isRTL ? "تمت إضافة الخبر بنجاح" : "News item added successfully");
        fetchItems();
        resetForm();
      }
    }
  };

  const handleDelete = async () => {
    if (!itemToDelete) return;
    
    const { error } = await supabase
      .from("news_ticker_items")
      .delete()
      .eq("id", itemToDelete);

    if (!error) {
      toast.success(isRTL ? "تم حذف الخبر بنجاح" : "News item deleted successfully");
      fetchItems();
    }
    setDeleteDialogOpen(false);
    setItemToDelete(null);
  };

  const toggleActive = async (id: string, currentStatus: boolean) => {
    const { error } = await supabase
      .from("news_ticker_items")
      .update({ is_active: !currentStatus })
      .eq("id", id);

    if (!error) {
      fetchItems();
      toast.success(isRTL ? "تم تحديث الحالة" : "Status updated");
    }
  };

  const resetForm = () => {
    setFormData({
      text_en: "",
      text_ar: "",
      category: "events",
      is_active: true,
    });
    setEditingItem(null);
    setDialogOpen(false);
  };

  const openEditDialog = (item: NewsItem) => {
    setEditingItem(item);
    setFormData({
      text_en: item.text_en,
      text_ar: item.text_ar,
      category: item.category as "events" | "courses" | "social",
      is_active: item.is_active,
    });
    setDialogOpen(true);
  };

  const filteredItems = filterCategory === "all" 
    ? items 
    : items.filter(item => item.category === filterCategory);

  const groupedItems = {
    events: filteredItems.filter(i => i.category === "events"),
    courses: filteredItems.filter(i => i.category === "courses"),
    social: filteredItems.filter(i => i.category === "social"),
  };

  return (
    <SidebarProvider>
      <div className="min-h-screen flex w-full" style={{background: 'linear-gradient(to bottom right, hsl(220 20% 10%), hsl(220 15% 15%), hsl(220 20% 10%))' }}>
        <AdminSidebar menuItems={dashboardItems} />
        <SidebarInset className="flex-1">
          <div className="p-4 md:p-6 max-w-6xl mx-auto">
            {/* Header */}
            <div className="flex items-center gap-4 mb-6">
              <SidebarTrigger className="md:hidden bg-primary text-primary-foreground p-2 rounded-lg">
                <Menu className="h-5 w-5" />
              </SidebarTrigger>
              <Link to="/admin-dashboard">
                <Button variant="ghost" size="icon">
                  <ArrowLeft className={`h-5 w-5 ${isRTL ? "rotate-180" : ""}`} />
                </Button>
              </Link>
              <div className="flex-1">
                <h1 className="text-2xl font-bold">
                  {isRTL ? "إدارة أشرطة الأخبار" : "News Ticker Management"}
                </h1>
                <p className="text-muted-foreground text-sm">
                  {isRTL ? "إضافة وتعديل وحذف أخبار الشريط المتحرك" : "Add, edit and delete news ticker items"}
                </p>
              </div>
              
              {/* Toggle Visibility Button */}
              <Button
                variant={tickerVisible ? "default" : "outline"}
                onClick={toggleTickerVisibility}
                className="gap-2"
              >
                {tickerVisible ? (
                  <>
                    <Eye className="h-4 w-4" />
                    {isRTL ? "ظاهر" : "Visible"}
                  </>
                ) : (
                  <>
                    <EyeOff className="h-4 w-4" />
                    {isRTL ? "مخفي" : "Hidden"}
                  </>
                )}
              </Button>
              
              <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open) resetForm(); else setDialogOpen(true); }}>
                <DialogTrigger asChild>
                  <Button className="gap-2">
                    <Plus className="h-4 w-4" />
                    {isRTL ? "إضافة خبر" : "Add News"}
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md">
                  <DialogHeader>
                    <DialogTitle>
                      {editingItem 
                        ? (isRTL ? "تعديل الخبر" : "Edit News Item")
                        : (isRTL ? "إضافة خبر جديد" : "Add New News Item")
                      }
                    </DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <div className="space-y-2">
                      <Label>{isRTL ? "النص بالإنجليزية" : "English Text"}</Label>
                      <Input
                        value={formData.text_en}
                        onChange={(e) => setFormData({ ...formData, text_en: e.target.value })}
                        placeholder="News text in English..."
                        dir="ltr"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>{isRTL ? "النص بالعربية" : "Arabic Text"}</Label>
                      <Input
                        value={formData.text_ar}
                        onChange={(e) => setFormData({ ...formData, text_ar: e.target.value })}
                        placeholder="نص الخبر بالعربية..."
                        dir="rtl"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>{isRTL ? "التصنيف" : "Category"}</Label>
                      <Select 
                        value={formData.category} 
                        onValueChange={(value: "events" | "courses" | "social") => 
                          setFormData({ ...formData, category: value })
                        }
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="events">
                            {isRTL ? "الفعاليات والمعارض" : "Events & Exhibitions"}
                          </SelectItem>
                          <SelectItem value="courses">
                            {isRTL ? "الدورات والوظائف" : "Courses & Jobs"}
                          </SelectItem>
                          <SelectItem value="social">
                            {isRTL ? "التواصل ومساحات X" : "Social & X Spaces"}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-center justify-between">
                      <Label>{isRTL ? "نشط" : "Active"}</Label>
                      <Switch
                        checked={formData.is_active}
                        onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                      />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={resetForm}>
                      {isRTL ? "إلغاء" : "Cancel"}
                    </Button>
                    <Button onClick={handleSubmit}>
                      {editingItem 
                        ? (isRTL ? "تحديث" : "Update")
                        : (isRTL ? "إضافة" : "Add")
                      }
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>

            {/* Filter */}
            <div className="flex items-center gap-3 mb-6">
              <Label>{isRTL ? "فلترة حسب:" : "Filter by:"}</Label>
              <Select value={filterCategory} onValueChange={setFilterCategory}>
                <SelectTrigger className="w-[200px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{isRTL ? "الكل" : "All"}</SelectItem>
                  <SelectItem value="events">{isRTL ? "الفعاليات والمعارض" : "Events & Exhibitions"}</SelectItem>
                  <SelectItem value="courses">{isRTL ? "الدورات والوظائف" : "Courses & Jobs"}</SelectItem>
                  <SelectItem value="social">{isRTL ? "التواصل ومساحات X" : "Social & X Spaces"}</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* News Items by Category */}
            {isLoading ? (
              <div className="text-center py-12 text-muted-foreground">
                {isRTL ? "جاري التحميل..." : "Loading..."}
              </div>
            ) : (
              <div className="space-y-6">
                {(filterCategory === "all" ? ["events", "courses", "social"] : [filterCategory]).map((cat) => {
                  const config = categoryConfig[cat as keyof typeof categoryConfig];
                  const categoryItems = groupedItems[cat as keyof typeof groupedItems];
                  const Icon = config.icon;
                  
                  if (categoryItems.length === 0 && filterCategory !== "all") return null;
                  
                  return (
                    <Card key={cat}>
                      <CardHeader className="pb-3">
                        <CardTitle className="flex items-center gap-2 text-lg">
                          <div className={`p-2 rounded-lg ${config.color}`}>
                            <Icon className="h-4 w-4" />
                          </div>
                          {isRTL ? config.labelAr : config.label}
                          <Badge variant="secondary" className="ml-auto">
                            {categoryItems.length}
                          </Badge>
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        {categoryItems.length === 0 ? (
                          <p className="text-sm text-muted-foreground text-center py-4">
                            {isRTL ? "لا توجد أخبار في هذا القسم" : "No news items in this category"}
                          </p>
                        ) : (
                          <div className="space-y-2">
                            {categoryItems.map((item) => (
                              <div 
                                key={item.id}
                                className="flex items-center gap-3 p-3 bg-muted/50 rounded-lg group hover:bg-muted transition-colors"
                              >
                                <GripVertical className="h-4 w-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity cursor-grab" />
                                <div className="flex-1 min-w-0">
                                  <p className="text-sm font-medium truncate">
                                    {isRTL ? item.text_ar : item.text_en}
                                  </p>
                                  <p className="text-xs text-muted-foreground truncate">
                                    {isRTL ? item.text_en : item.text_ar}
                                  </p>
                                </div>
                                <Switch
                                  checked={item.is_active}
                                  onCheckedChange={() => toggleActive(item.id, item.is_active)}
                                />
                                <Button 
                                  variant="ghost" 
                                  size="icon"
                                  onClick={() => openEditDialog(item)}
                                >
                                  <Pencil className="h-4 w-4" />
                                </Button>
                                <Button 
                                  variant="ghost" 
                                  size="icon"
                                  className="text-destructive hover:text-destructive"
                                  onClick={() => {
                                    setItemToDelete(item.id);
                                    setDeleteDialogOpen(true);
                                  }}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            ))}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </div>
        </SidebarInset>
      </div>

      {/* Delete Confirmation */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {isRTL ? "تأكيد الحذف" : "Confirm Delete"}
            </AlertDialogTitle>
            <AlertDialogDescription>
              {isRTL 
                ? "هل أنت متأكد من حذف هذا الخبر؟ لا يمكن التراجع عن هذا الإجراء."
                : "Are you sure you want to delete this news item? This action cannot be undone."
              }
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{isRTL ? "إلغاء" : "Cancel"}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
              {isRTL ? "حذف" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </SidebarProvider>
  );
};

export default NewsTicker;
